import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import dayjs from "dayjs";
import { MobiSimRegistrationInjection } from "features/mobiSimRegistration/di/MobiSimRegistrationInjection";
import { MobiSimRegistrationQueryEntity } from "features/mobiSimRegistration/domain/entities/MobiSimRegistrationEntity";
import { MobiSimRegistrationItemEntity } from "features/mobiSimRegistration/domain/entities/MobiSimRegistrationItemEntity";
import isEmpty from "lodash/isEmpty";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { strings } from "utils/localizedStrings";
import { IMobiSimRegistrationObservedState } from "../interfaces/IMobiSimRegistration";

export interface IDropdownActionRecord {
  record: MobiSimRegistrationItemEntity;
  action: string;
}

export type DetailSelectionType =
  | MobiSimRegistrationItemEntity
  | IDropdownActionRecord;

type FilterOptionsState = Pick<
  IMobiSimRegistrationObservedState,
  "storeOptions" | "staffOptions" | "userOptions"
>;

class MobiSimRegistrationInput implements Disposable {
  public fetchList = new BehaviorSubject<MobiSimRegistrationQueryEntity>(
    new MobiSimRegistrationQueryEntity()
  );
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchPackage = new BehaviorSubject<BaseFilterListRequestModel>({});
  public exportExcel = new BehaviorSubject<MobiSimRegistrationQueryEntity>(
    new MobiSimRegistrationQueryEntity()
  );
  public fetchDetail = new BehaviorSubject<MobiSimRegistrationQueryEntity>(
    new MobiSimRegistrationQueryEntity()
  );
  public selectDetail = new BehaviorSubject<DetailSelectionType>({});
  public syncFilterOptions = new BehaviorSubject<Partial<FilterOptionsState>>(
    {}
  );
  public checkStatus = new BehaviorSubject<string>("");
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});
  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchCarrier.complete();
    this.fetchPackage.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.selectDetail.complete();
    this.syncFilterOptions.complete();
    this.checkStatus.complete();
    this.fetchPartners.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.loadUsers.complete();
  }
}

class MobiSimRegistrationOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public onCheckStatusSuccess = new BehaviorSubject<boolean>(false);
  public onDetailLoaded = new BehaviorSubject<{ action?: string } | null>(null);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onCheckStatusSuccess.complete();
    this.onDetailLoaded.complete();
  }
}

@injectable()
export class MobiSimRegistrationViewModel extends BasedObservedViewModel<
  MobiSimRegistrationInput,
  MobiSimRegistrationOutput,
  IMobiSimRegistrationObservedState
> {
  private mobiSimRegistrationUseCase =
    MobiSimRegistrationInjection.getMobiSimRegistrationUseCase();

  protected override createObservedState(): IMobiSimRegistrationObservedState {
    return {
      list: [],
      detail: {},
      pagination: { page: 0, total: 0, limit: 10 },
      loading: false,
      detailLoading: false,
      statusLoading: {},
      carrier: [],
      packageList: [],
      partnerOptions: [],
      storeOptions: [],
      staffOptions: [],
      userOptions: [{ label: strings.GlobalLabels.All, value: "" }],
      summary: {},
    };
  }

  constructor() {
    super(new MobiSimRegistrationInput(), new MobiSimRegistrationOutput());
  }

  private downloadExcel(data: Blob): void {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYYMMDD_HHmmss");
    link.href = url;
    link.setAttribute("download", `SIM_REPORT_${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private toPartnerOptions(items: any[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({
        label: `${item.name}${
          item.providerCode ? ` - ${item.providerCode.toUpperCase()}` : ""
        }`,
        value: item.code,
        type: item.type,
        isStore: item.isStore,
        providerCode: item.providerCode,
      })),
    ];
  }

  private toStoreOptions(items: any[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({
        label: item.store_name,
        value: item.store_code,
      })),
    ];
  }

  private toUserOptions(items: BaseUserResponseModel[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({ label: item.name, value: item.id })),
    ];
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params, guard) => {
      try {
        guard?.runIfLatest(() => {
          this.setObservedState({
            loading: true,
            list: [],
            summary: {},
            pagination: {
              page: params.page ?? this.observedState.pagination.page,
              limit: params.limit ?? this.observedState.pagination.limit,
              total: this.observedState.pagination.total,
            },
          });
        });
        const res = await this.mobiSimRegistrationUseCase.getList(params);
        guard?.runIfLatest(() => {
          if (res?.data) {
            this.setObservedState({
              list: res.data,
              pagination: res.pagination ?? {},
              summary: res.summary ?? {},
            });
          }
        });
      } catch (error) {
        console.error(error);
        guard?.runIfLatest(() => this.setObservedState({ list: [] }));
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.mobiSimRegistrationUseCase.getFilterList(
          params,
          "carrier"
        );
        const carrier = (res ?? []).filter(
          (item) =>
            item.code === "viettel" ||
            item.code === "mobifone" ||
            item.code === "vinaphone"
        );
        this.setObservedState({ carrier });
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchPackage, async (params) => {
      try {
        const res = await this.mobiSimRegistrationUseCase.getFilterList(
          params,
          "package"
        );
        const packageList = Array.from(
          new Map((res ?? []).map((item) => [item.code, item])).values()
        );
        this.setObservedState({ packageList });
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params) => {
      try {
        const cache = productCache.getCache(CacheKeyEnum.partnerList);
        if (!isEmpty(cache)) {
          this.setObservedState({
            partnerOptions: this.toPartnerOptions(cache),
          });
          return;
        }
        const res = await this.mobiSimRegistrationUseCase.getFilterList(
          params,
          "partner"
        );
        const data = res ?? [];
        this.setObservedState({
          partnerOptions: this.toPartnerOptions(data),
        });
        productCache.saveCache({
          key: CacheKeyEnum.partnerList,
          product: data,
        });
      } catch (error) {
        console.error("Error fetching partners:", error);
        this.setObservedState({ partnerOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params) => {
      try {
        if (!params.partnerCode) return;
        const res = await this.mobiSimRegistrationUseCase.getFilterList(
          params,
          "store"
        );
        this.setObservedState({ storeOptions: this.toStoreOptions(res ?? []) });
      } catch (error) {
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params) => {
      if (isEmpty(params.storeCode)) {
        this.setObservedState({ staffOptions: [] });
        return;
      }
      try {
        const res = await this.mobiSimRegistrationUseCase.getUserForFilter(
          params,
          "staff"
        );
        const allUsers = (res ?? []).flatMap((item: any) => item?.users ?? []);
        const mappedUsers = allUsers.map((user: any) => ({
          id: user.id,
          name: user.name || user.userName || user.email || user.phone || "",
          code: user.code,
        }));
        this.setObservedState({
          staffOptions: this.toUserOptions(mappedUsers),
        });
      } catch (error) {
        this.setObservedState({ staffOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadUsers, async (params) => {
      try {
        const filterType =
          params.providerCode === "mobitopup" || params.providerCode === "smm"
            ? "staff"
            : "userB2B";
        const res = await this.mobiSimRegistrationUseCase.getUserForFilter(
          params,
          filterType
        );
        const allUsers = (res ?? []).flatMap((item: any) => item?.users ?? []);
        const mappedUsers = allUsers.map((user: any) => ({
          id: user.id,
          name: user.fullName || user.email || user.name || user.phone || "",
          code: user.code,
        }));
        this.setObservedState({
          userOptions: this.toUserOptions(mappedUsers),
        });
      } catch (error) {
        this.setObservedState({ userOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.setObservedState({ detailLoading: true });
        const res = await this.mobiSimRegistrationUseCase.getDetail(params);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
          this.output.onDetailLoaded.next({});
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    this.skipFirstInitial(this.input.selectDetail, (selection) => {
      if ("action" in selection) {
        this.setObservedState({ detail: selection.record });
        this.output.onDetailLoaded.next({ action: selection.action });
        return;
      }
      this.setObservedState({ detail: selection });
      this.output.onDetailLoaded.next({});
    });

    this.skipFirstInitial(this.input.syncFilterOptions, (options) => {
      this.setObservedState(options);
    });

    this.skipFirstInitial(this.input.exportExcel, async (params, guard) => {
      try {
        guard?.runIfLatest(() => this.setObservedState({ loading: true }));
        const res = await this.mobiSimRegistrationUseCase.exportReport(params);
        if (res?.data) {
          if (res.data.size < 1000) {
            this.output.onError.next({
              message: strings.GlobalMessage.ExcelError,
            });
            return;
          }
          this.downloadExcel(res.data);
          this.output.onExportSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.checkStatus, async (code) => {
      if (!code) return;
      try {
        this.setObservedState((previous) => ({
          ...previous,
          statusLoading: { ...previous.statusLoading, [code]: true },
        }));
        const res = await this.mobiSimRegistrationUseCase.checkStatus(code);
        if (res?.data) {
          this.output.onCheckStatusSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        this.setObservedState((previous) => ({
          ...previous,
          statusLoading: { ...previous.statusLoading, [code]: false },
        }));
      }
    });
  }
}
